import {
  compact,
  filter,
  first,
  groupBy,
  isEmpty,
  keyBy,
  last,
  map,
  orderBy,
  replace,
  startsWith,
  toPairs,
} from "lodash/fp";

import {
  getMatchValue,
  MATCH_END_DELIMITER,
  MATCH_START_DELIMITER,
} from "@app/analytics/MeetingReports/components/SearchResult/utils";
import type { SearchResultMatch } from "@app/analytics/types";
import type {
  TranscriptKeyword,
  TranscriptSummary,
  TranscriptSummaryItem,
  TranscriptWord,
  SessionChapter,
  SessionNote,
  TranscriptTurnParagraph,
} from "@app/common/types";
import { isWithinRange } from "@app/common/utils";
import { replaceAt } from "@app/utils/arrays";

import {
  MeetingReportSearchResult,
  NoteSearchResult,
  NoteSearchResultMap,
  NoteSearchResultNoteType,
  SearchType,
  TranscriptSearchResult,
} from "../types";

const KEYWORD_SEARCH_PREFIX = "keyword:";
const END_PUNCTUATION_REGEX = /[!?.,]$/;

type WordCharacterMap = { [key: string]: number };
// these are the only fields we care about when searching
type SearchableNoteItem = {
  id: string;
  value: string;
  type: NoteSearchResult["note"]["type"];
};
// these are the only fields we care about when searching
type SearchableTranscriptSummary = Pick<
  TranscriptSummary,
  "actionItems" | "keyQuestions" | "topics" | "chapters" | "summary"
>;

export interface SearchOptions {
  /** If true, will append a "search" query param onto the query param search */
  appendQueryParam?: boolean;
  /** When passed, will limit search to only the specified speakers */
  speakers?: string[];
  /** When passed, will limit search to only the specified turn ids */
  turns?: string[];
  /** When true, will match on full word/phrase match only (e.g. "um" will match "um" but not "momentum" */
  exactPhrase?: boolean;
  searchNotes?: boolean;
  searchTranscript?: boolean;
  searchType?: SearchType;
}

export const isKeywordSearch = startsWith(KEYWORD_SEARCH_PREFIX);
export const appendKeywordSearchPrefix = (searchTerm: string) =>
  `${KEYWORD_SEARCH_PREFIX}${searchTerm}`;
export const removeKeywordSearchPrefix = replace(KEYWORD_SEARCH_PREFIX, "");

/**
 * Creates a map of "word id" -> "character in the phrase at which the word starts".
 * Having this map makes it much easier to find the word that intersects with a
 * single character index when generating search results
 *
 * Example input:
 *    [
 *      { id: 1, value: "hello" },
 *      { id: 2, value: "my" },
 *      { id: 3, value: "name" },
 *      { id: 4, value: "is" },
 *      { id: 5, value: "bob" }
 *    ]
 *
 * Example output:
 *    {
 *      1: 0,
 *      2: 6,
 *      3: 9,
 *      ...
 *    }
 * */
const createWordCharacterMap = (words: TranscriptWord[]): WordCharacterMap => {
  let fullText = "";
  return words.reduce(
    (wordMap, word) => {
      const currentWordStartIndex = fullText.length;
      fullText += ` ${word.value}`;

      wordMap[word.id] = currentWordStartIndex;
      return wordMap;
    },
    {} as { [key: string]: number }
  );
};

/**
 * Returns the word that intersects a particular character index
 *
 * Example:
 *   word character map:
 *     {
 *        word_1: 0,
 *        word_2: 6,
 *        word_3: 9,
 *        ...
 *        word_11: 65,
 *      }
 *
 *  index of 7 => word_2
 *  index of 70 => word_11
 * */
const getWordAtCharacterIndex = (
  wordCharacterMap: WordCharacterMap,
  index: number
) => {
  const wordIntersectingIndex = toPairs(wordCharacterMap).find(
    (_, i, entries) => {
      const nextWordEntry = entries[i + 1];
      if (nextWordEntry) {
        const nextWordStartIndex = nextWordEntry[1];

        // if the start index of the next word is larger than the index we're looking for,
        // that means that the current word must be the word we're looking for
        return nextWordStartIndex > index;
      }

      // if there's no next word, we can reasonably assume that this word is the
      // intersecting word
      return true;
    }
  );

  return wordIntersectingIndex![0];
};

const getAllWordIdsInSearchResult = (
  startWordId: string,
  endWordId: string,
  words: TranscriptWord[]
) => {
  // short-circuit this method if they're the same word
  if (startWordId === endWordId) {
    return [startWordId];
  }

  const startWord = words.find((word) => word.id === startWordId);
  const endWord = words.find((word) => word.id === endWordId);

  const startWordIndex = startWord ? words.indexOf(startWord) : 0;
  const endWordIndex = endWord ? words.indexOf(endWord) : words.length;

  return map("id", words.slice(startWordIndex, endWordIndex + 1));
};

const performKeywordSearch = (
  keyword: TranscriptKeyword,
  paragraphs: TranscriptTurnParagraph[],
  startIndex = 0
): TranscriptSearchResult[] => {
  const results: TranscriptSearchResult[] = [];

  if (isEmpty(keyword.occurrences)) return results;

  let searchResultIndex = startIndex;

  keyword.occurrences.forEach((occurrence) => {
    const allParagraphsInOccurrence: TranscriptTurnParagraph[] = [];
    const allWordsInOccurrence: TranscriptWord[] = [];

    let currentParagraphIndex = 0;
    let currentParagraph = paragraphs[currentParagraphIndex];

    while (
      currentParagraph &&
      (currentParagraph.rawStartTime.toMillis() <= occurrence.startTime ||
        currentParagraph.rawEndTime.toMillis() >= occurrence.endTime)
    ) {
      // TODO (ENG-10852): may need to do something smarter here than just
      //  ignoring words without start/end times
      const turnWordsInKeywordOccurrence = currentParagraph.words.filter(
        (word) =>
          word.startTime != null &&
          word.endTime != null &&
          occurrence.startTime <= word.startTime &&
          occurrence.endTime >= word.endTime
      );

      if (!isEmpty(turnWordsInKeywordOccurrence)) {
        allParagraphsInOccurrence.push(currentParagraph);
        allWordsInOccurrence.push(...turnWordsInKeywordOccurrence);
      }

      currentParagraphIndex += 1;
      currentParagraph = paragraphs[currentParagraphIndex];
    }

    if (isEmpty(allWordsInOccurrence)) return;

    const lastWord = last(allWordsInOccurrence)!;

    const result: TranscriptSearchResult = {
      type: "transcript_search_result",
      index: searchResultIndex,
      match: {
        start: {
          paragraph: allParagraphsInOccurrence[0].id,
          word: allWordsInOccurrence[0].id,
          index: 0,
        },
        end: {
          paragraph: last(allParagraphsInOccurrence)!.id,
          word: lastWord.id,
          index: lastWord.value.length,
        },
      },
      paragraphs: map("id", allParagraphsInOccurrence),
      words: map("id", allWordsInOccurrence),
    };

    results.push(result);
    searchResultIndex += 1;
  });

  return results;
};

const performExactPhraseTranscriptSearch = (
  searchTerm: string,
  paragraph: TranscriptTurnParagraph,
  searchResultStartIndex: number
) => {
  const searchResultsForParagraph: TranscriptSearchResult[] = [];

  // running tally; used for determining which search result is "active" when paginating
  // through the results
  let searchResultIndex = searchResultStartIndex;
  const removeEndPunctuation = !searchTerm.match(END_PUNCTUATION_REGEX);
  const numWordsInSearchTerm = searchTerm.split(/\s/).length;

  for (let i = 0; i <= paragraph.words.length - numWordsInSearchTerm; i++) {
    const compareWords = paragraph.words.slice(i, i + numWordsInSearchTerm);
    const wordOrPhrase = map("value", compareWords).join(" ");
    const valueToCompare = removeEndPunctuation
      ? wordOrPhrase.replace(END_PUNCTUATION_REGEX, "")
      : wordOrPhrase;

    if (valueToCompare.toLowerCase() === searchTerm) {
      const lastWord = last(compareWords)!;
      const result: TranscriptSearchResult = {
        type: "transcript_search_result",
        index: searchResultIndex,
        match: {
          start: {
            paragraph: paragraph.id,
            word: first(compareWords)!.id,
            index: 0,
          },
          end: {
            paragraph: paragraph.id,
            word: lastWord.id,
            index: removeEndPunctuation
              ? lastWord.value.replace(END_PUNCTUATION_REGEX, "").length
              : lastWord.value.length,
          },
        },
        paragraphs: [paragraph.id],
        words: map("id", compareWords),
      };

      searchResultIndex += 1;
      searchResultsForParagraph.push(result);

      if (numWordsInSearchTerm > 1) {
        // jump to after this match if we're matching on a phrase
        i = i + numWordsInSearchTerm - 1;
      }
    }
  }

  return searchResultsForParagraph;
};

const performStandardTranscriptSearch = (
  searchTerm: string,
  paragraph: TranscriptTurnParagraph,
  searchResultStartIndex: number
) => {
  const searchResultsForParagraph: TranscriptSearchResult[] = [];
  let searchResultIndex = searchResultStartIndex;

  // use the turn's entire text for finding search results so that we can easily
  // match across multiple words
  const searchString = paragraph.text.toLowerCase();
  const termLength = searchTerm.length;
  // can only match across multiple words if there's a space in the term somewhere!
  const willMatchMultipleWords = searchTerm.includes(" ");

  // TODO: match across multiple turns
  // TODO: exclude punctuation (e.g. "hello, sir" == "hello sir")

  let startIndex = 0;
  let matchIndex = searchString.indexOf(searchTerm);

  if (matchIndex < 0) return searchResultsForParagraph;

  const wordCharacterMap = createWordCharacterMap(paragraph.words);

  while (matchIndex > -1) {
    const startMatchWordId = getWordAtCharacterIndex(
      wordCharacterMap,
      matchIndex
    );

    // the match index relative to the "start" word. these relative indices are important
    // for the case in which reaction annotations and search result highlighting need to
    // occur simultaneously; reactions are split up by words, so having individual word
    // references in the search results make chunking up by reaction *and* search results
    // more straightforward. and we still need to keep track of the overall start/end match
    // index for the purposes of finding all (and no duplicate!) search results in the turn
    //   Example:
    //     phrase = "hello my name is bob"
    //     search term = "ame"
    //     match index (index relative to the turn text) = 10
    //     start word match index (index relative to the word "name") = 1
    const startWordMatchIndex = matchIndex - wordCharacterMap[startMatchWordId];

    const endMatchIndex = matchIndex + termLength;
    const endMatchWordId = willMatchMultipleWords
      ? getWordAtCharacterIndex(wordCharacterMap, endMatchIndex)
      : startMatchWordId;

    // same as start word match index; the "end" index relative to the "end" word
    const endWordMatchIndex = willMatchMultipleWords
      ? endMatchIndex - wordCharacterMap[endMatchWordId]
      : startWordMatchIndex + termLength;

    const result: TranscriptSearchResult = {
      type: "transcript_search_result",
      index: searchResultIndex,
      match: {
        start: {
          paragraph: paragraph.id,
          word: startMatchWordId,
          index: startWordMatchIndex,
        },
        end: {
          paragraph: paragraph.id,
          word: endMatchWordId,
          index: endWordMatchIndex,
        },
      },
      paragraphs: [paragraph.id],
      words: getAllWordIdsInSearchResult(
        startMatchWordId,
        endMatchWordId,
        paragraph.words
      ),
    };

    searchResultIndex += 1;
    searchResultsForParagraph.push(result);

    startIndex = endMatchIndex;

    // continue searching while ignoring any portion of the text that we've
    // already matched against
    matchIndex = searchString.indexOf(searchTerm, startIndex);
  }

  return searchResultsForParagraph;
};

export const searchTranscript = (
  searchTerm: string,
  paragraphs: TranscriptTurnParagraph[],
  keywords: TranscriptKeyword[],
  startIndex = 0,
  opts?: SearchOptions
): TranscriptSearchResult[] => {
  const results: TranscriptSearchResult[] = [];

  if (!searchTerm || isEmpty(paragraphs)) return results;

  let paragraphsToSearch = paragraphs;

  if (opts?.speakers || opts?.turns) {
    // filter turns down to specified speakers and/or turns. filter is an OR operation, such that
    // the turn must either have its turn id in the list of turns OR the turn's speaker must be in
    // the list of speakers
    paragraphsToSearch = paragraphsToSearch.filter(
      (p) =>
        (p.turn.speaker && (opts?.speakers ?? []).includes(p.turn.speaker)) ||
        (opts?.turns ?? []).includes(p.turn.id)
    );
  }

  const caseInsensitiveSearchTerm = searchTerm.toLowerCase().trim();

  if (isKeywordSearch(caseInsensitiveSearchTerm)) {
    const keywordValue = removeKeywordSearchPrefix(caseInsensitiveSearchTerm);
    const keyword = keywords.find((keyword) => keyword.value === keywordValue);
    return keyword
      ? performKeywordSearch(keyword, paragraphsToSearch, startIndex)
      : results;
  }

  const searchFunction = opts?.exactPhrase
    ? performExactPhraseTranscriptSearch
    : performStandardTranscriptSearch;

  let searchResultIndex = startIndex;

  paragraphsToSearch.forEach((turn) => {
    const resultsForParagraph = searchFunction(
      caseInsensitiveSearchTerm,
      turn,
      searchResultIndex
    );
    results.push(...resultsForParagraph);
    searchResultIndex += resultsForParagraph.length;
  });

  return results;
};

const performExactPhraseSummaryItemSearch = (
  searchTerm: string,
  item: SearchableNoteItem,
  searchResultStartIndex = 0
) => {
  const results: NoteSearchResult[] = [];
  const words = item.value.toLowerCase().trim().split(/\s/);

  const removeEndPunctuation = !searchTerm.match(END_PUNCTUATION_REGEX);
  let searchResultIndex = searchResultStartIndex;

  const numWordsInSearchTerm = searchTerm.split(/\s/).length;

  for (let i = 0; i <= words.length - numWordsInSearchTerm; i++) {
    const compareWords = words.slice(i, i + numWordsInSearchTerm);
    const wordOrPhrase = compareWords.join(" ");

    const valueToCompare = removeEndPunctuation
      ? wordOrPhrase.replace(END_PUNCTUATION_REGEX, "")
      : wordOrPhrase;

    if (valueToCompare === searchTerm) {
      const textBeforeThisWordOrPhrase = words.slice(0, i).join(" ");
      const startIndexMatch = (textBeforeThisWordOrPhrase.length || -1) + 1; // +1 to account for the extra space before this word

      const result: NoteSearchResult = {
        type: "note_search_result",
        index: searchResultIndex,
        note: {
          id: item.id,
          type: item.type,
        },
        match: {
          start: { index: startIndexMatch },
          end: { index: startIndexMatch + valueToCompare.length },
        },
      };

      searchResultIndex += 1;
      results.push(result);
    }
  }

  return results;
};

const performStandardSummaryItemSearch = (
  searchTerm: string,
  item: SearchableNoteItem,
  searchResultStartIndex = 0
) => {
  const results: NoteSearchResult[] = [];

  const searchString = item.value.toLowerCase().trim();
  const termLength = searchTerm.length;

  let matchStartIndex = searchString.indexOf(searchTerm);
  let searchResultIndex = searchResultStartIndex;

  while (matchStartIndex > -1) {
    const matchEndIndex = matchStartIndex + termLength;
    const result: NoteSearchResult = {
      type: "note_search_result",
      index: searchResultIndex,
      note: {
        id: item.id,
        type: item.type,
      },
      match: {
        start: { index: matchStartIndex },
        end: { index: matchEndIndex },
      },
    };

    searchResultIndex += 1;
    results.push(result);
    matchStartIndex = searchString.indexOf(searchTerm, matchEndIndex);
  }

  return results;
};

export const getChapterTitleSearchResultKey = (chapter: SessionChapter) =>
  `${chapter.id}-title`;
export const getChapterDescriptionSearchResultKey = (chapter: SessionChapter) =>
  `${chapter.id}-description`;
const isChapterSearchResult = (searchResult: NoteSearchResult) =>
  searchResult.note.type === "chapter";
export const getNoteIdFromSearchResultId = (searchResult: NoteSearchResult) => {
  if (isChapterSearchResult(searchResult)) {
    return searchResult.note.id.split("-")[0];
  }
  return searchResult.note.id;
};

export const searchNotes = (
  searchTerm: string,
  summary: SearchableTranscriptSummary,
  sessionNotes: SessionNote[],
  opts?: SearchOptions
) => {
  const results: NoteSearchResult[] = [];
  let caseInsensitiveSearchTerm = searchTerm.toLowerCase().trim();

  if (!caseInsensitiveSearchTerm) return results;

  // can't do the same kind of keyword search in the notes as we do in the transcript, so just parse
  // out the specific term and do a regular search against it
  if (isKeywordSearch(caseInsensitiveSearchTerm)) {
    caseInsensitiveSearchTerm = removeKeywordSearchPrefix(
      caseInsensitiveSearchTerm
    ).toLowerCase();
  }

  const searchFunction = opts?.exactPhrase
    ? performExactPhraseSummaryItemSearch
    : performStandardSummaryItemSearch;

  // the order of results is important; the indices on the results need to ascend according
  // to the order each section appears in the UI:
  //   1. summary
  //   2. chapters & topics
  //   3. action items
  //   4. key questions
  //   5. session notes

  results.push(...searchFunction(caseInsensitiveSearchTerm, summary.summary));

  if (isEmpty(summary.chapters)) {
    // if there are no chapters, perform a straightforward topic search
    summary.topics.forEach((t) => {
      results.push(
        ...searchFunction(caseInsensitiveSearchTerm, t, results.length)
      );
    });
  } else {
    const topicsById = keyBy("id", summary.topics);
    // if there are chapters, first search the chapter title, then the chapter description, then each
    // of the topics under the chapter in order
    summary.chapters.forEach((c) => {
      results.push(
        ...searchFunction(
          caseInsensitiveSearchTerm,
          {
            id: getChapterTitleSearchResultKey(c),
            value: c.title,
            type: "chapter",
          },
          results.length
        )
      );

      results.push(
        ...searchFunction(
          caseInsensitiveSearchTerm,
          {
            id: getChapterDescriptionSearchResultKey(c),
            value: c.description,
            type: "chapter",
          },
          results.length
        )
      );
      c.topics.forEach((t) => {
        // map over the chapter topics but search against the topic from the
        // summary object; in edit mode we don't keep the chapter topics in sync
        // when making changes (we just update the summary topics directly), so
        // this ensures the search works properly in both readonly and edit mode
        const topic = topicsById[t.id];
        results.push(
          ...searchFunction(caseInsensitiveSearchTerm, topic, results.length)
        );
      });
    });
  }

  summary.actionItems.forEach((a) => {
    results.push(
      ...searchFunction(caseInsensitiveSearchTerm, a, results.length)
    );
  });

  summary.keyQuestions.forEach((q) => {
    results.push(
      ...searchFunction(caseInsensitiveSearchTerm, q, results.length)
    );
  });

  sessionNotes.forEach((n) => {
    results.push(
      ...searchFunction(
        caseInsensitiveSearchTerm,
        { ...n, type: "note" },
        results.length
      )
    );
  });

  return results;
};

/** *********************************************
 *  NEW SEARCH UTILS
 * **********************************************/

const sanitizeText = (text: string, removeDelimiters = false) =>
  removeDelimiters
    ? text.replace(/{{|}}|\.{3}/g, "")
    : text.replace(/\.{3}/g, "");

const replaceSubstring = (
  text: string,
  replacement: string,
  position: { start: number; end: number }
) =>
  compact([
    text.slice(0, position.start),
    replacement,
    text.slice(position.end),
  ]).join("");

const compactResults = (results: NoteSearchResult[]) => {
  const compactedResults: NoteSearchResult[] = [];

  let currentIndex = 0;
  let currentResult = results[currentIndex];
  let nextResult = results[currentIndex + 1];

  while (nextResult) {
    const resultsOverlap = isWithinRange(
      nextResult.match.start.index,
      [currentResult.match.start.index, currentResult.match.end.index],
      { inclusiveStart: false, inclusiveEnd: false }
    );

    if (resultsOverlap) {
      currentResult = {
        ...currentResult,
        match: {
          start: { index: currentResult.match.start.index },
          end: { index: nextResult.match.end.index },
        },
      };
      currentIndex += 1;
      nextResult = results[currentIndex + 1];
    } else {
      compactedResults.push(currentResult);
      currentIndex += 1;
      currentResult = nextResult;
      nextResult = results[currentIndex + 1];
    }
  }

  if (currentResult) {
    compactedResults.push(currentResult);
  }

  return compactedResults;
};

export const sortAndCompactResults = (
  results: NoteSearchResult[],
  indexStart: number
) => {
  const sortedResults = orderBy("match.start.index", "asc", results);
  const compactedResults = compactResults(sortedResults);
  return compactedResults.map((result, i) => ({
    ...result,
    index: indexStart + i,
  }));
};

const getResultsFromMatch = (
  item: SearchableNoteItem,
  match: SearchResultMatch
) => {
  // get the raw text of the match, so we can find where it is in the full text
  // e.g. "...foo {{bar}} baz..." => "foo bar baz"
  const matchText = sanitizeText(match.text, true);
  const phraseMatchIndex = item.value.indexOf(matchText);

  const results: NoteSearchResult[] = [];

  if (phraseMatchIndex < 0) return results;

  // insert the match text into the item
  // e.g. "blah blah foo bar baz blah blah" => "blah blah foo {{bar}} baz blah blah"
  let replacedText = replaceSubstring(item.value, sanitizeText(match.text), {
    start: phraseMatchIndex,
    end: phraseMatchIndex + matchText.length,
  });

  let matchStartIndex = replacedText.indexOf(MATCH_START_DELIMITER);
  let matchEndIndex =
    replacedText.indexOf(MATCH_END_DELIMITER) + MATCH_END_DELIMITER.length;

  while (matchStartIndex > -1) {
    // find each match and generate a search result from the match
    const matchValue = replacedText.slice(matchStartIndex, matchEndIndex);
    const searchResult: NoteSearchResult = {
      type: "note_search_result",
      index: 0, // this will get set when results are sorted/compacted in the method that called this
      note: {
        id: item.id,
        type: item.type,
      },
      match: {
        start: { index: matchStartIndex },
        // end index = the end index as it was found - the delimiters (as they will get stripped out)
        end: { index: matchEndIndex - MATCH_END_DELIMITER.length * 2 },
      },
    };

    results.push(searchResult);

    // replace the match we just found in the string, so we can find the next one
    replacedText = replaceSubstring(replacedText, getMatchValue(matchValue), {
      start: matchStartIndex,
      end: matchEndIndex,
    });
    // and find the next one, if there is one
    matchStartIndex = replacedText.indexOf(MATCH_START_DELIMITER);
    matchEndIndex =
      replacedText.indexOf(MATCH_END_DELIMITER) + MATCH_END_DELIMITER.length;
  }

  return results;
};

const getSummarySearchResults = (
  summary: TranscriptSummary["summary"],
  matches: SearchResultMatch[]
) => {
  const summaryMatches = filter({ category: "summary" }, matches);
  const searchResults: NoteSearchResult[] = [];

  // short-circuit if there are no summary matches
  if (isEmpty(summaryMatches)) return searchResults;

  summaryMatches.forEach((match) => {
    const results = getResultsFromMatch(summary, match);
    searchResults.push(...results);
  });

  return sortAndCompactResults(searchResults, 0);
};

const getNotesSearchResults = (
  notes: SessionNote[],
  matches: SearchResultMatch[],
  indexStart: number
) => {
  const allNoteMatches = filter({ category: "note" }, matches);
  const searchResults: NoteSearchResult[] = [];

  // short-circuit if there are no note matches
  if (isEmpty(allNoteMatches)) return searchResults;

  notes.forEach((note) => {
    const matchesForNote = filter({ id: note.id }, allNoteMatches);
    const resultsForNote: NoteSearchResult[] = [];

    matchesForNote.forEach((match) => {
      const results = getResultsFromMatch({ ...note, type: "note" }, match);
      resultsForNote.push(...results);
    });

    const sortedResultsForNote = sortAndCompactResults(
      resultsForNote,
      indexStart + searchResults.length
    );

    searchResults.push(...sortedResultsForNote);
  });

  return searchResults;
};

const getSummaryItemSearchResults = (
  items: TranscriptSummaryItem[],
  matches: SearchResultMatch[],
  category: "action_item" | "key_question" | "topic",
  indexStart: number
) => {
  const matchesForCategory = filter({ category }, matches);
  const searchResults: NoteSearchResult[] = [];

  // short-circuit if there are no matches in this category
  if (isEmpty(matchesForCategory)) return searchResults;

  items.forEach((item) => {
    const matchesForItem = filter({ id: item.id }, matchesForCategory);
    const resultsForItem: NoteSearchResult[] = [];

    matchesForItem.forEach((match) => {
      const results = getResultsFromMatch(item, match);
      resultsForItem.push(...results);
    });

    const sortedResultsForItem = sortAndCompactResults(
      resultsForItem,
      indexStart + searchResults.length
    );

    searchResults.push(...sortedResultsForItem);
  });

  return searchResults;
};

const getChapterSearchResults = (
  chapters: SessionChapter[],
  matches: SearchResultMatch[],
  indexStart: number
) => {
  const chapterMatches = filter({ category: "chapter" }, matches);
  const results: NoteSearchResult[] = [];

  chapters.forEach((chapter) => {
    const matchesForChapter = filter({ id: chapter.id }, chapterMatches);

    const titleMatches: NoteSearchResult[] = [];
    const descriptionMatches: NoteSearchResult[] = [];

    // 1. look for matches in the title
    matchesForChapter.forEach((match) => {
      const results = getResultsFromMatch(
        {
          id: getChapterTitleSearchResultKey(chapter),
          value: chapter.title,
          type: "chapter",
        },
        match
      );

      titleMatches.push(...results);
    });

    results.push(
      ...sortAndCompactResults(titleMatches, indexStart + results.length)
    );

    // 2. look for matches in the description
    matchesForChapter.forEach((match) => {
      const results = getResultsFromMatch(
        {
          id: getChapterDescriptionSearchResultKey(chapter),
          value: chapter.description,
          type: "chapter",
        },
        match
      );

      descriptionMatches.push(...results);
    });

    results.push(
      ...sortAndCompactResults(descriptionMatches, indexStart + results.length)
    );

    // 3. look for matches in the topics
    const chapterTopicMatches = getSummaryItemSearchResults(
      chapter.topics,
      matches,
      "topic",
      indexStart + results.length
    );

    // these are already sorted/compacted
    results.push(...chapterTopicMatches);
  });

  return results;
};

/**
 * Transforms search result matches (those that come from a BE search) into
 * `NoteSearchResult`s, a format understood by the components that
 * chunk/annotate content.
 * */
export const transformNoteSearchResults = (
  matches: SearchResultMatch[],
  summary: TranscriptSummary,
  notes: SessionNote[]
): NoteSearchResult[] => {
  const results: NoteSearchResult[] = [];

  // short-circuit if there's nothing to transform
  if (isEmpty(matches)) return results;

  // the order of results is important; the indices on the results need to ascend according
  // to the order each section appears in the UI:
  //   1. summary
  //   2. chapters & topics
  //        a. chapter title
  //        b. chapter description
  //        c. chapter topics
  //   3. action items
  //   4. key questions
  //   5. session notes

  results.push(...getSummarySearchResults(summary.summary, matches));

  if (isEmpty(summary.chapters)) {
    // if there are no chapters, just look for topic matches
    results.push(
      ...getSummaryItemSearchResults(
        summary.topics,
        matches,
        "topic",
        results.length
      )
    );
  } else {
    results.push(
      ...getChapterSearchResults(summary.chapters, matches, results.length)
    );
  }

  results.push(
    ...getSummaryItemSearchResults(
      summary.actionItems,
      matches,
      "action_item",
      results.length
    )
  );

  results.push(
    ...getSummaryItemSearchResults(
      summary.keyQuestions,
      matches,
      "key_question",
      results.length
    )
  );

  results.push(...getNotesSearchResults(notes, matches, results.length));

  return results;
};

/**
 * Transforms search result matches (those that come from a BE search) into
 * `TranscriptSearchResult`s, a format understood by the components that
 * chunk/annotate content.
 * */
export const transformTranscriptSearchResults = (
  matches: SearchResultMatch[],
  paragraphs: TranscriptTurnParagraph[],
  indexStart = 0
): TranscriptSearchResult[] => {
  const allTranscriptMatches = filter({ category: "turn" }, matches);

  // short circuit if there aren't any transcript matches
  if (isEmpty(allTranscriptMatches)) return [];

  const matchesByTurnId = groupBy("id", allTranscriptMatches);

  const allTurnSearchResults: TranscriptSearchResult[] = [];

  // TODO (ENG-8256) - does this work across paragraphs?
  paragraphs.forEach((paragraph) => {
    const matchesForTurn = matchesByTurnId[paragraph.turn.id];
    if (isEmpty(matchesForTurn)) return;

    const resultsForParagraph: TranscriptSearchResult[] = [];
    let paragraphText = paragraph.text;

    // 1. go through all the matches for this paragraph and insert them into
    // their correct spot in the full text
    matchesForTurn.forEach((match) => {
      // get the raw text of the match, so we can find where it is in the full text
      // e.g. "...foo {{bar}} baz..." => "foo bar baz"
      const matchText = sanitizeText(match.text, true);
      const phraseMatchIndex = paragraphText.indexOf(matchText);

      if (phraseMatchIndex < 0) return;

      paragraphText = replaceSubstring(
        paragraphText,
        sanitizeText(match.text),
        {
          start: phraseMatchIndex,
          end: phraseMatchIndex + matchText.length,
        }
      );
    });

    let paragraphWords = paragraphText.split(/\s/);

    // 2. go through each word and create a search result for each matching word (or phrase)
    for (let i = 0; i < paragraphWords.length; i++) {
      const word = paragraphWords[i];

      if (!word.includes(MATCH_START_DELIMITER)) {
        // if this word was not part of a match, ignore it and move on to the next one
        continue;
      }

      const startIndex = word.indexOf(MATCH_START_DELIMITER);
      let endWord = word;
      let endIndex = -1;
      let endWordIndex = -1;

      const wordContainsMatchEnd = word.includes(MATCH_END_DELIMITER);

      if (wordContainsMatchEnd) {
        endIndex =
          word.indexOf(MATCH_END_DELIMITER) - MATCH_START_DELIMITER.length;
        endWordIndex = i;
      } else {
        endWord = paragraphWords
          .slice(i)
          .find((w) => w.includes(MATCH_END_DELIMITER))!;
        endWordIndex = paragraphWords.indexOf(endWord, i);
        endIndex = endWord.indexOf(MATCH_END_DELIMITER);
      }

      const result: TranscriptSearchResult = {
        type: "transcript_search_result",
        index:
          indexStart + allTurnSearchResults.length + resultsForParagraph.length,
        match: {
          start: {
            paragraph: paragraph.id,
            word: paragraph.words[i].id,
            index: startIndex,
          },
          end: {
            paragraph: paragraph.id,
            word: paragraph.words[endWordIndex].id,
            index: endIndex,
          },
        },
        paragraphs: [paragraph.id],
        words: map("id", paragraph.words.slice(i, endWordIndex + 1)),
      };

      resultsForParagraph.push(result);

      if (wordContainsMatchEnd) {
        // if the word contains the match end, it's not unreasonable that the word could also
        // have a second (or third, fourth, etc.) match within it
        const strippedWord = word
          .replace(MATCH_START_DELIMITER, "")
          .replace(MATCH_END_DELIMITER, "");
        if (strippedWord.indexOf(MATCH_START_DELIMITER) > -1) {
          // if there is indeed another match inside this word, replace the word
          // with the remaining word fragment and decrement so the next run through this loop will pick it up
          paragraphWords = replaceAt(paragraphWords, strippedWord, i);
          i--;
        }
      } else if (endWord.indexOf(MATCH_START_DELIMITER, endIndex) > -1) {
        // similarly, if the end word was a different word than the start word, and the end word also has
        // another match it in, replace the word with the remaining end word fragment and jump the loop
        // to that word
        const strippedWord = endWord.replace(MATCH_END_DELIMITER, "");
        paragraphWords = replaceAt(paragraphWords, strippedWord, endWordIndex);
        i = endWordIndex - 1;
      }
    }

    allTurnSearchResults.push(...resultsForParagraph);
  });

  return allTurnSearchResults;
};

export const isTranscriptSearchResult = (
  result: MeetingReportSearchResult
): result is TranscriptSearchResult =>
  result.type === "transcript_search_result";

export const isNoteSearchResult = (
  result: MeetingReportSearchResult
): result is NoteSearchResult => result.type === "note_search_result";

export const getSearchResultsByParagraph = (
  results: MeetingReportSearchResult[]
) => {
  const resultsByParagraph = new Map<string, TranscriptSearchResult[]>();
  const transcriptSearchResults = filter(isTranscriptSearchResult, results);

  transcriptSearchResults.forEach((result) => {
    result.paragraphs.forEach((paragraphId) => {
      const currentResultsForTurn = resultsByParagraph.get(paragraphId) ?? [];
      resultsByParagraph.set(paragraphId, [...currentResultsForTurn, result]);
    });
  });

  return resultsByParagraph;
};

export const INITIAL_NOTE_SEARCH_RESULTS_MAP: NoteSearchResultMap = {
  summary: new Map<string, NoteSearchResult[]>(),
  chapter: new Map<string, NoteSearchResult[]>(),
  topic: new Map<string, NoteSearchResult[]>(),
  action_item: new Map<string, NoteSearchResult[]>(),
  key_question: new Map<string, NoteSearchResult[]>(),
  note: new Map<string, NoteSearchResult[]>(),
};

const getInitialResultsByTypeAndId = () => {
  return Object.entries(INITIAL_NOTE_SEARCH_RESULTS_MAP).reduce(
    (obj, [type, typeMap]) => {
      return {
        ...obj,
        // have to actually copy the maps since just trying to spread
        // INITIAL_NOTE_SEARCH_RESULTS_MAP doesn't break the map object
        // reference
        [type as NoteSearchResultNoteType]: new Map(typeMap),
      };
    },
    {} as typeof INITIAL_NOTE_SEARCH_RESULTS_MAP
  );
};

export const getSearchResultsByNoteTypeAndId = (
  results: MeetingReportSearchResult[]
) => {
  const resultsByTypeAndId = getInitialResultsByTypeAndId();
  const noteSearchResults = filter(isNoteSearchResult, results);

  noteSearchResults.forEach((result) => {
    const currentResultsByType = resultsByTypeAndId[result.note.type];
    const resultsForNote = currentResultsByType.get(result.note.id) ?? [];
    currentResultsByType.set(result.note.id, [...resultsForNote, result]);
  });

  return resultsByTypeAndId;
};
